/**
 * CLI Sandbox Management
 *
 * Create, provision, and manage sandbox templates and instances.
 * Drive template authoring through artifact uploads, command execution,
 * and diagnostics — designed for Claude Code to iterate on sandbox setup.
 */

import process from 'node:process';
import { readFileSync } from 'node:fs';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, selectProperty } from './cli-helpers.js';
import { apiFetch, isTerminalFailure, exitOnFailure } from './api-fetch.js';
import { SANDBOX_GUIDE_SECTIONS, printGuide } from '@chatsdet/content';
import {
  INSTANCE_SIZES,
  INSTANCE_SIZE_SPEC,
  SANDBOX_EGRESS_MODES,
  parseInstanceSize,
  type InstanceSize,
  type SandboxEgressAllowlist,
  type SandboxEgressMode,
} from '@chatsdet/types';
import { getPluginIds } from '@chatsdet/emulation/plugins';
import { handleEmulation } from './sandbox-emulation.js';
import { handleLogs } from './sandbox-logs.js';
export {
  withEmulationMaster,
  withPluginConfig,
  withPluginEnabled,
  withoutPlugin,
} from './sandbox-emulation.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Template = {
  id: string;
  name: string;
  propertyId: string | null;
  repositoryFullName: string;
  branch: string;
  subdirectory: string | null;
  status: string;
  checklistProfile: string | null;
  configSchema: Array<{ key: string; description?: string; required: boolean; sensitive: boolean }> | null;
  configDefaults: Record<string, string> | null;
  configuredSensitiveKeys?: string[];
  configuredSensitiveAutoConfigKeys?: string[];
  createdAt: string;
  updatedAt: string;
};

type GitHubRepo = {
  id: string;
  repoFullName: string;
  repoName: string;
  defaultBranch: string;
  rootDir: string | null;
  agentHint: string | null;
};

type Instance = {
  id: string;
  templateId: string;
  name: string;
  status: string;
  hostUrl: string | null;
  isPrivate: boolean;
  expiresAt: string | null;
  createdAt: string;
  /**
   * What the instance is for. `flow_authoring` is the org-shared instance the
   * Flow Designer auto-spins and that sandbox workflow runs reuse — surfaced in
   * `sandbox list` so agents can find/warm it.
   */
  purpose?: string | null;
};

export const getArgValues = (argv: string[], flag: string): string[] => {
  const values: string[] = [];
  for (let i = 0; i < argv.length; i += 1) {
    if (argv[i] === flag && argv[i + 1] && !argv[i + 1]!.startsWith('-')) {
      values.push(argv[i + 1]!);
    } else if (argv[i]?.startsWith(`${flag}=`)) {
      values.push(argv[i]!.slice(flag.length + 1));
    }
  }
  return values;
};

export const parseEgressMode = (value: string | undefined): SandboxEgressMode => {
  if (!value) return 'off';
  if ((SANDBOX_EGRESS_MODES as readonly string[]).includes(value)) {
    return value as SandboxEgressMode;
  }
  console.error(
    `Error: invalid --egress-mode '${value}'. Must be one of: ${SANDBOX_EGRESS_MODES.join(', ')}`
  );
  process.exit(1);
};

export const parseEgressAllowlist = (argv: string[]): SandboxEgressAllowlist => {
  const rawEntries = getArgValues(argv, '--egress-allow')
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

  return rawEntries.map((entry) => {
    const separator = entry.indexOf(':');
    if (separator <= 0) {
      console.error(
        `Error: invalid --egress-allow '${entry}'. Use domain:example.com or cidr:203.0.113.0/24`
      );
      process.exit(1);
    }
    const kind = entry.slice(0, separator);
    const value = entry.slice(separator + 1);
    if (kind !== 'domain' && kind !== 'cidr') {
      console.error(`Error: invalid --egress-allow kind '${kind}'. Must be domain or cidr.`);
      process.exit(1);
    }
    return { kind, value };
  });
};

type Artifact = {
  id: string;
  taskId: string;
  artifactType: string;
  filename: string;
  content?: string;
  createdAt: string;
  updatedAt: string;
};

type ChecklistItem = {
  id: string;
  label: string;
  description: string;
  dependsOn: string[];
  producesArtifacts: string[];
  agentAutomatable: boolean;
  itemStatus: { status: string; startedAt?: string; completedAt?: string; summary?: string; error?: string };
  actionable: boolean;
  artifacts: Array<{ artifactType: string; filename: string }>;
};

type CommandOrError =
  | { stdout: string; stderr: string; exitCode: number }
  | { error: string; code?: string };

type DiagnosticsResult = {
  instance: Record<string, unknown>;
  lifecycle?: {
    databaseStatus: string;
    poolStatus: string | null;
    ec2InstanceState: string | null;
    live: boolean;
    message: string | null;
  };
  docker: CommandOrError;
  git: CommandOrError;
  disk: CommandOrError;
  resources?: CommandOrError;
  agentHealth: CommandOrError;
  appHealth: CommandOrError;
  provision?: {
    phase: string;
    jobStatus: string;
    startedAt?: string;
    elapsedSeconds: number | null;
    lastError?: string;
  };
};

type ServiceRoute = { service: string; route: string; port: string };

async function fetchInstanceRoutes(apiUrl: string, token: string, instanceId: string): Promise<ServiceRoute[]> {
  const res = await apiRequest<{ ok: boolean; error?: string; data: { routes: ServiceRoute[] } }>(
    apiUrl, token, 'GET', `/org/environments/instances/${instanceId}/routes`,
    undefined,
    { timeoutMs: 30_000 }
  );
  return res.ok ? res.data.routes : [];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_ICONS: Record<string, string> = {
  completed: '[done]',
  running: '[....]',
  pending: '[    ]',
  failed: '[FAIL]',
  skipped: '[skip]',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function pad(s: string, n: number): string {
  return s.padEnd(n);
}

const INSTANCE_SIZE_HELP_LINES = INSTANCE_SIZES.map((size) => {
  const spec = INSTANCE_SIZE_SPEC[size];
  return `  ${pad(size, 8)} ${spec.ec2InstanceType} · ${spec.vcpu} vCPU · ${spec.memoryMiB / 1024} GiB`;
});

// ---------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------

function printHelp(): void {
  console.log(
    [
      'canary sandbox: Manage sandbox templates and instances',
      '',
      'Sub-commands:',
      '  guide [section]                          Show sandbox authoring guide',
      '  list                                    List templates and instances',
      '  template [id]                           Show template details',
      '  repo list --template <id>               List repos linked to a template',
      '  repo add --template <id> --property-github-repo-id <uuid> [--role <r>] [--spec <r=owner/name@branch:subdir>]',
      '  repo update --template <id> --link-id <uuid> [--role <r>] [--branch <b>] [--subdirectory <s>] [--service-name <n>]',
      '  repo remove --template <id> --link-id <uuid>',
      '  checklist <template-id>                 Show checklist state',
      '  checklist skip <template-id> <item-id>  Skip a checklist item',
      '  checklist complete <template-id> <item-id> [options]  Mark item complete from CLI',
      '  artifacts <template-id>                 List artifacts',
      '  artifact get <template-id> <type>       Get artifact content',
      '  artifact create <template-id> <type> <file>  Upload artifact (use --path for file type)',
      '  create [property] [options]              Create template from property (triggers provisioning)',
      '  provision <template-id> [options]       Create a dedicated instance from a verified template',
      '  warm <template-id> [--wait] [--timeout <sec>]  Warm the org-shared flow-authoring instance',
      '                                          (the one the flow designer auto-spins; sandbox runs reuse it)',
      '  verify <template-id>                    Verify an authoring template (provision + probe + flip to verified)',
      '  set-size <template-id> <size>           Set EC2 size for next provision (see "Instance sizes" below)',
      '  pool set-size <template-id> <count>     Set the warm stopped-pool size for a template (0 = off)',
      '  build <instance-id>                     Build and start app services on sandbox',
      '  run-command <instance-id> <command...>  Execute shell command on sandbox',
      '    --service <name>  Run inside a Docker container instead of on the host VM',
      '  diagnostics <instance-id>               Show sandbox diagnostics',
      '  logs <instance-id> [options]            Show container logs (live if running, else the captured failure snapshot)',
      '  terminate <instance-id>                 Terminate an instance',
      '  config list <template-id>               Show config schema and defaults',
      '  config set <template-id> <key> <value>  Set a config default value',
      '  config unset <template-id> <key>        Remove a config default value',
      '  emulation get <template-id>             Show emulation config + available plugins',
      '  emulation enable/disable <template-id> [pluginId]  Toggle master switch (or one plugin)',
      '  emulation set <template-id> <pluginId> [opts]      Configure a plugin entry',
      '  emulation remove <template-id> <pluginId>          Drop a plugin entry',
      '  action <list|get|create|delete|execute|executions>  Manage template actions (see "Action sub-commands" below)',
      '  delete <template-id>                    Delete template and all instances',
      '',
      'Create options:',
      '  --property <name|id>  Property (prompted if omitted, auto-selected if only one)',
      '  --branch <branch>     Branch (default: from linked repo)',
      '  --name <name>         Template name (default: repo name)',
      '  --subdirectory <path> Subdirectory (default: from linked repo rootDir)',
      '  --size <size>         EC2 size for first authoring sandbox (default: medium; see "Instance sizes" below)',
      '',
      'Provision options:',
      '  --name <name>         Instance name',
      '  --ttl <hours>         Time to live in hours (default: 8)',
      '  --with-agent          Start with sandbox agent',
      '  --private             Skip public ALB routing',
      '  --egress-mode <mode>  off|observe (observe logs unmatched egress only; default: off)',
      '  --egress-allow <kind:value>  Repeat or comma-separate domain:/cidr: entries',
      '',
      'Artifact get options:',
      '  --output <file>       Write to file instead of stdout',
      '',
      'Logs options:',
      '  --service <name>      Service name (default: all services; live path only)',
      '  --tail <n>            Number of lines (default: 200; live path only)',
      '  --failure             Force the captured failure snapshot (full logs + deploy.log)',
      '                        Works for failed/terminated instances after the host is gone.',
      '                        Running instances auto-use live logs; failed ones auto-fall back',
      '                        to the snapshot when the live tail can\'t run.',
      '',
      'Guide sections:',
      '  goal, research, artifacts, actions, verify, checklist',
      '',
      'Instance sizes:',
      ...INSTANCE_SIZE_HELP_LINES,
      '',
      'Action sub-commands:',
      '  action list <template-id>                    List actions for a template',
      '  action get <template-id> <slug>              Show action definition + script',
      '  action create <template-id> <slug> [opts]    Create or update an action',
      '  action delete <template-id> <slug>           Delete an action',
      '  action execute <instance-id> <slug> [opts]   Execute action on a running instance',
      '  action executions <instance-id> [opts]       List execution history',
      '  action execution get <inst-id> <exec-id>     Show one execution (advances a background run)',
      '  action execution cancel <inst-id> <exec-id>  Stop a background execution',
      '',
      'Action execute options:',
      '  --param key=value       Set an input param (repeatable)',
      '  --params-file <file>    JSON file of input params (--param overrides)',
      '  --async                 Return once the script is running, without waiting.',
      '                          Required for anything longer than the 300s',
      '                          synchronous ceiling. Poll `action execution get`',
      '                          to reach a terminal status.',
      '  --timeout-ms <ms>       Remote ceiling for an --async run (default 1h, max 48h)',
      '',
      'Action create options:',
      '  --label <label>         Display name (default: slug → Title Case)',
      '  --description <text>    Description',
      '  --icon <icon>           Phosphor icon name',
      '  --script <file>         Path to script file (bash)',
      '  --input-schema <file>   Path to JSON Schema file for input params',
      '  --output-schema <file>  Path to JSON Schema file for output',
      '  --credential-output-config <file>  JSON file with ActionCredentialOutputConfig (auto-creates a Canary credential from the action result).',
      '  --output-config <file>  JSON file with an array of output slots the action hands back (test-results / artifacts-archive).',
      '                          Supports `variableMapping` (persona var name → $output.* token) to fill the persona\'s declared variables ($var.USER.<NAME>)',
      '  --credential-template <id>  Credential template this action provisions; login nodes using it auto-provision',
      '  --provisioning-mode <shared|unique>  shared = provision once / reused (default); unique = one credential per run (script must accept a runId input param)',
      '  --run-on-provision      Base seed: run automatically once per provisioned instance (not login-triggered). Its --output-schema keys are exposed to flows as $var.SEED.<key>',
      '  --provision-order <n>   Order for multiple base seeds on a template (ascending; unset runs first)',
      '  --timeout <ms>          Timeout in ms (default: 55000)',
      '  --encrypt-script        Encrypt script content at rest',
      '',
      'Action execute options:',
      '  --param <key>=<value>   Set parameter (repeatable)',
      '  --params-file <file>    JSON file with all params',
      '',
      'Emulation sub-commands (third-party service emulation: Slack/Jira/Google-OAuth/email):',
      '  emulation get <template-id>                       Master switch, configured plugins, available catalog',
      '  emulation enable <template-id> [pluginId]         Turn on emulation (or enable a single plugin)',
      '  emulation disable <template-id> [pluginId]        Turn off emulation (or disable a single plugin)',
      '  emulation set <template-id> <pluginId> [opts]     Create/update a plugin entry (does not flip master switch)',
      '  emulation remove <template-id> <pluginId>         Remove a plugin entry',
      `  Available plugins: ${getPluginIds().join(', ')}`,
      '',
      'Emulation set options:',
      '  --config <file>       JSON file with plugin-specific config (e.g. { "tenant": "acme" })',
      '  --fixture <file>      JSON file with seed/fixture data for the plugin',
      '  --env-mapping <file>  JSON file mapping logical env-contract keys to the app env var(s)',
      '',
      'Global options:',
      '  --json                Output raw JSON',
      '  --env <env>           Environment (prod, dev, local)',
      '  -h, --help            Show help',
    ].join('\n')
  );
}

// ---------------------------------------------------------------------------
// Sub-command handlers
// ---------------------------------------------------------------------------

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');

  const [templatesRes, instancesRes] = await Promise.all([
    apiRequest<{ ok: boolean; error?: string; data: Template[] }>(
      apiUrl, token, 'GET', '/org/environments/templates'
    ),
    apiRequest<{ ok: boolean; error?: string; data: Instance[] }>(
      apiUrl, token, 'GET', '/org/environments/instances'
    ),
  ]);

  if (!templatesRes.ok) {
    console.error(`Error: ${templatesRes.error}`);
    process.exit(1);
  }
  if (!instancesRes.ok) {
    console.error(`Error loading instances: ${instancesRes.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify({ templates: templatesRes.data, instances: instancesRes.data }, null, 2));
    return;
  }

  const templates = templatesRes.data ?? [];
  const instances = instancesRes.data ?? [];

  console.log('Templates:');
  if (templates.length === 0) {
    console.log('  (none)');
  } else {
    console.log(`  ${pad('ID', 38)} ${pad('Name', 24)} ${pad('Status', 14)} ${pad('Profile', 12)} Repository`);
    for (const t of templates) {
      console.log(`  ${pad(t.id, 38)} ${pad(t.name, 24)} ${pad(t.status, 14)} ${pad(t.checklistProfile ?? '-', 12)} ${t.repositoryFullName}:${t.branch}`);
    }
  }

  console.log('');
  console.log('Instances:');
  if (instances.length === 0) {
    console.log('  (none)');
  } else {
    console.log(`  ${pad('ID', 38)} ${pad('Status', 14)} ${pad('Purpose', 16)} ${pad('Access', 10)} ${pad('Template', 38)} Expires`);
    for (const i of instances) {
      const access = i.isPrivate ? 'private' : 'public';
      // The org-shared flow-authoring instance is the one workflow runs reuse —
      // flag it so it's easy to spot (and warm via `sandbox warm <template>`).
      const purpose =
        i.purpose === 'flow_authoring' ? 'flow_authoring*' : (i.purpose ?? '-');
      console.log(`  ${pad(i.id, 38)} ${pad(i.status, 14)} ${pad(purpose, 16)} ${pad(access, 10)} ${pad(i.templateId, 38)} ${i.expiresAt ? formatDate(i.expiresAt) : '-'}`);
    }
    console.log('');
    console.log('  * flow_authoring = org-shared instance the flow designer auto-spins and');
    console.log('    sandbox workflow runs reuse. Warm it with: canary sandbox warm <template-id>');
  }
}

async function handleTemplate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = argv.filter((a) => !a.startsWith('-'));
  const json = hasFlag(argv, '--json');

  if (!id) {
    await handleList(argv, apiUrl, token);
    return;
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: Template }>(
    apiUrl, token, 'GET', `/org/environments/templates/${id}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const t = res.data;
  console.log(`Template: ${t.name}`);
  console.log(`  ID:         ${t.id}`);
  console.log(`  Repository: ${t.repositoryFullName}:${t.branch}`);
  if (t.subdirectory) console.log(`  Subdir:     ${t.subdirectory}`);
  console.log(`  Status:     ${t.status}`);
  console.log(`  Profile:    ${t.checklistProfile ?? 'full-stack (default)'}`);
  console.log(`  Created:    ${formatDate(t.createdAt)}`);
  console.log(`  Updated:    ${formatDate(t.updatedAt)}`);

  if (t.configSchema && t.configSchema.length > 0) {
    console.log('');
    console.log('  Config Schema:');
    for (const c of t.configSchema) {
      const flags = [c.required ? 'required' : 'optional', c.sensitive ? 'sensitive' : ''].filter(Boolean).join(', ');
      console.log(`    ${c.key} (${flags})${c.description ? ` — ${c.description}` : ''}`);
    }
  }
}

async function handleArtifacts(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [templateId] = argv.filter((a) => !a.startsWith('-'));
  const json = hasFlag(argv, '--json');

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox artifacts <template-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: Artifact[] }>(
    apiUrl, token, 'GET', `/org/environments/templates/${templateId}/artifacts`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const artifacts = res.data ?? [];
  if (artifacts.length === 0) {
    console.log('No artifacts found.');
    return;
  }

  console.log(`${pad('Type', 22)} ${pad('Filename', 30)} ${pad('Task', 24)} Created`);
  for (const a of artifacts) {
    console.log(`${pad(a.artifactType, 22)} ${pad(a.filename, 30)} ${pad(a.taskId, 24)} ${formatDate(a.createdAt)}`);
  }
}

async function handleChecklist(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const json = hasFlag(argv, '--json');

  // Handle checklist sub-commands: skip, complete
  if (positional[0] === 'skip') {
    await handleChecklistSkip(argv.slice(1), apiUrl, token);
    return;
  }
  if (positional[0] === 'complete') {
    await handleChecklistComplete(argv.slice(1), apiUrl, token);
    return;
  }

  const templateId = positional[0];
  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox checklist <template-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: ChecklistItem[] }>(
    apiUrl, token, 'GET', `/org/environments/templates/${templateId}/checklist`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const items = res.data ?? [];
  if (items.length === 0) {
    console.log('No checklist items.');
    return;
  }

  console.log('Checklist:');
  for (const item of items) {
    const icon = STATUS_ICONS[item.itemStatus.status] ?? '[????]';
    const actionable = item.actionable ? ' *' : '';
    console.log(`  ${icon} ${item.label}${actionable}`);
    if (item.itemStatus.summary) {
      console.log(`         ${item.itemStatus.summary}`);
    }
    if (item.itemStatus.error) {
      console.log(`         ERROR: ${item.itemStatus.error}`);
    }
    if (item.artifacts.length > 0) {
      console.log(`         Artifacts: ${item.artifacts.map((a) => a.artifactType).join(', ')}`);
    }
  }
  console.log('');
  console.log('  * = actionable (deps met, ready to run)');
}

async function handleChecklistSkip(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const itemId = positional[1];

  if (!templateId || !itemId) {
    console.error('Error: both template ID and item ID required. Usage: canary sandbox checklist skip <template-id> <item-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl, token, 'POST', `/org/environments/templates/${templateId}/checklist/${itemId}/skip`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Skipped checklist item: ${itemId}`);
}

async function handleChecklistComplete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const itemId = positional[1];

  const json = hasFlag(argv, '--json');

  if (!templateId || !itemId) {
    console.error('Error: both template ID and item ID required.');
    console.error('Usage: canary sandbox checklist complete <template-id> <item-id> [--status success|failed] [--summary "..."]');
    process.exit(1);
  }

  const status = getArgValue(argv, '--status') ?? 'success';
  const summary = getArgValue(argv, '--summary');
  const configSchemaArg = getArgValue(argv, '--config-schema');
  const healthCheckPath = getArgValue(argv, '--health-check-path');

  let configSchema: unknown;
  if (configSchemaArg) {
    // Accept inline JSON or a file path
    try {
      configSchema = JSON.parse(configSchemaArg);
    } catch {
      try {
        configSchema = JSON.parse(readFileSync(configSchemaArg, 'utf-8'));
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.error(`Error reading config schema from "${configSchemaArg}": ${msg}`);
        process.exit(1);
      }
    }
  }

  const body: Record<string, unknown> = {
    status: status === 'failed' ? 'failed' : 'completed',
    ...(summary ? { summary } : {}),
    ...(configSchema ? { configSchema } : {}),
    ...(healthCheckPath ? { healthCheckPath } : {}),
  };

  const res = await apiRequest<{ ok: boolean; error?: string; data: { itemId: string; status: string } }>(
    apiUrl, token, 'POST', `/org/environments/templates/${templateId}/checklist/${itemId}/complete`, body
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }
  console.log(`Completed checklist item: ${itemId} (${res.data?.status})`);
}

async function handleConfigList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox config list <template-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: Template }>(
    apiUrl, token, 'GET', `/org/environments/templates/${templateId}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  const t = res.data;
  const schema = t.configSchema ?? [];
  const defaults = t.configDefaults ?? {};
  const configuredSensitiveKeys = t.configuredSensitiveKeys ?? [];
  const configuredSensitiveKeySet = new Set(configuredSensitiveKeys);

  if (json) {
    console.log(
      JSON.stringify(
        { configSchema: schema, configDefaults: defaults, configuredSensitiveKeys },
        null,
        2
      )
    );
    return;
  }

  if (schema.length === 0) {
    console.log('No config schema defined for this template.');
    return;
  }

  console.log('Config Schema:');
  for (const item of schema) {
    const value = defaults[item.key];
    const display = item.sensitive
      ? configuredSensitiveKeySet.has(item.key)
        ? '•••••••• (configured)'
        : '(not set)'
      : (value ?? '(not set)');
    const required = item.required ? ' (required)' : '';
    const sensitive = item.sensitive ? ' [sensitive]' : '';
    console.log(`  ${item.key}${required}${sensitive}`);
    if (item.description) console.log(`    ${item.description}`);
    console.log(`    Value: ${display}`);
  }
}

export const configSetPayload = (key: string, value: string) => ({
  configDefaults: { [key]: value },
  unsetKeys: [] as string[],
});

export const configUnsetPayload = (key: string) => ({
  configDefaults: {} as Record<string, string>,
  unsetKeys: [key],
});

async function handleConfigSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const fromStdin = hasFlag(argv, '--value-stdin');
  const templateId = positional[0];
  const key = positional[1];
  let value = positional[2];

  if (!templateId || !key) {
    console.error('Error: template ID and key required.');
    console.error('Usage: canary sandbox config set <template-id> <key> <value>');
    console.error('  For sensitive values: echo "sk-ant-..." | canary sandbox config set <template-id> <key> --value-stdin');
    process.exit(1);
  }

  if (fromStdin) {
    const chunks: Buffer[] = [];
    for await (const chunk of process.stdin) chunks.push(chunk as Buffer);
    value = Buffer.concat(chunks).toString('utf-8').trim();
  }

  if (value === undefined) {
    console.error('Error: value required. Pass as argument or use --value-stdin');
    process.exit(1);
  }

  // The server may take longer than 30s to acknowledge config writes
  // (encrypted columns, audit log). Even when it does the write succeeds —
  // bump the CLI ceiling so the user sees the success rather than a
  // misleading timeout.
  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl, token, 'PATCH', `/org/environments/templates/${templateId}/config`, {
      ...configSetPayload(key, value),
    },
    { timeoutMs: 90_000 }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Config set: ${key}`);
}

async function handleConfigUnset(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [templateId, key] = argv.filter((arg) => !arg.startsWith('-'));
  if (!templateId || !key) {
    console.error('Usage: canary sandbox config unset <template-id> <key>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'PATCH',
    `/org/environments/templates/${templateId}/config`,
    configUnsetPayload(key),
    { timeoutMs: 90_000 }
  );
  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Config unset: ${key}`);
}

async function handleSetSize(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [templateId, sizeArg] = argv.filter((a) => !a.startsWith('-'));

  if (!templateId || !sizeArg) {
    console.error('Error: template ID and size required.');
    console.error(`Usage: canary sandbox set-size <template-id> <${INSTANCE_SIZES.join('|')}>`);
    process.exit(1);
  }

  const size = parseInstanceSize(sizeArg);
  if (!size) {
    console.error(`Error: invalid size '${sizeArg}'. Must be one of: ${INSTANCE_SIZES.join(', ')}`);
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; instanceSize?: InstanceSize }>(
    apiUrl,
    token,
    'PATCH',
    `/org/environments/templates/${templateId}/instance-size`,
    { instanceSize: size }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Instance size set to ${size}`);
}

async function handlePoolSetSize(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [templateId, countArg] = argv.filter((a) => !a.startsWith('-'));

  if (!templateId || countArg === undefined) {
    console.error('Error: template ID and count required.');
    console.error('Usage: canary sandbox pool set-size <template-id> <count>');
    process.exit(1);
  }

  // Strict decimal-integer parse — reject '1e2', '0x10', '3.5', etc. that
  // Number() would silently accept, so the count the operator typed is exactly
  // what the server receives.
  const poolMinReady = /^\d+$/.test(countArg) ? Number(countArg) : Number.NaN;
  if (!Number.isInteger(poolMinReady)) {
    console.error(`Error: invalid count '${countArg}'. Must be a non-negative integer.`);
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; poolMinReady?: number }>(
    apiUrl,
    token,
    'PATCH',
    `/org/environments/templates/${templateId}/pool-min-ready`,
    { poolMinReady }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Warm stopped-pool size set to ${poolMinReady}`);
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const json = hasFlag(argv, '--json');

  // Resolve property — positional arg or --property flag, prompted if omitted
  const propertyHint = positional[0] ?? getArgValue(argv, '--property');
  const property = await selectProperty(apiUrl, token, propertyHint ?? undefined);
  if (!property) process.exit(1);

  // Fetch linked repos for the property
  const reposRes = await apiRequest<{ ok: boolean; error?: string; items: GitHubRepo[] }>(
    apiUrl, token, 'GET', `/org/properties/${property.id}/github-repos`
  );
  if (!reposRes.ok || !reposRes.items?.length) {
    console.error(`No GitHub repos linked to property "${property.name}". Link one first in the web app.`);
    process.exit(1);
  }

  const linkedRepo = reposRes.items[0];
  const branch = getArgValue(argv, '--branch') ?? linkedRepo.defaultBranch ?? 'main';
  const name = getArgValue(argv, '--name') ?? linkedRepo.repoName ?? linkedRepo.repoFullName.split('/').pop() ?? property.name;
  const subdirectory = getArgValue(argv, '--subdirectory') ?? linkedRepo.rootDir ?? undefined;

  const sizeArg = getArgValue(argv, '--size');
  const instanceSize = sizeArg ? parseInstanceSize(sizeArg) : undefined;
  if (sizeArg && !instanceSize) {
    console.error(`Error: invalid --size '${sizeArg}'. Must be one of: ${INSTANCE_SIZES.join(', ')}`);
    process.exit(1);
  }

  const body: Record<string, unknown> = {
    name,
    propertyId: property.id,
    repositoryFullName: linkedRepo.repoFullName,
    branch,
  };
  if (subdirectory) body.subdirectory = subdirectory;
  if (linkedRepo.agentHint) body.hints = linkedRepo.agentHint;
  if (instanceSize) body.instanceSize = instanceSize;

  const res = await apiRequest<{ ok: boolean; error?: string; data: Template }>(
    apiUrl, token, 'POST', '/org/environments/templates', body
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  console.log(`Template created: ${res.data.id}`);
  console.log(`  Property: ${property.name}`);
  console.log(`  Repository: ${res.data.repositoryFullName}:${res.data.branch}`);
  console.log(`  Status: ${res.data.status}`);
  console.log('');
  console.log('Authoring job enqueued. Poll status with:');
  console.log(`  canary sandbox template ${res.data.id}`);
}

async function handleProvision(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox provision <template-id> [--ttl 8]');
    process.exit(1);
  }

  const name = getArgValue(argv, '--name') ?? `instance-${Date.now()}`;
  const ttl = parseInt(getArgValue(argv, '--ttl') ?? '8', 10);
  const withAgent = hasFlag(argv, '--with-agent');
  const isPrivate = hasFlag(argv, '--private');
  const egressMode = parseEgressMode(getArgValue(argv, '--egress-mode'));
  const egressAllowlist = parseEgressAllowlist(argv);

  const body: Record<string, unknown> = {
    templateId,
    name,
    expiresInHours: ttl,
    userConfig: {},
    withAgent,
    isPrivate,
    egressMode,
    egressAllowlist,
  };

  const res = await apiRequest<{ ok: boolean; error?: string; data: Instance }>(
    apiUrl, token, 'POST', '/org/environments/instances', body
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  console.log(`Instance created: ${res.data.id}`);
  console.log(`  Status: ${res.data.status}`);
  if (isPrivate) console.log(`  Access: private (no public URL)`);
  if (egressMode !== 'off') {
    console.log(`  Egress: ${egressMode} (${egressAllowlist.length} allowlist entries)`);
  }
  console.log(`  Expires: ${res.data.expiresAt ? formatDate(res.data.expiresAt) : '-'}`);
}

type AuthoringInstance = {
  instanceId: string;
  status: string;
  url: string | null;
  expiresAt: string | null;
  createdAt: string | null;
  created: boolean;
};

/**
 * Warm (get-or-create) the org-shared flow-authoring instance for a template —
 * the same instance the Flow Designer auto-spins and that a sandbox workflow run
 * reuses. Pre-warming avoids the first run blocking on a cold provision.
 *
 * `--wait` polls the idempotent ensure endpoint (safe: it reuses the live
 * instance and just slides the TTL) until the instance is running, fails, or
 * the timeout elapses.
 */
async function handleWarm(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const json = hasFlag(argv, '--json');
  const wait = hasFlag(argv, '--wait');
  const timeoutSec = parseInt(getArgValue(argv, '--timeout') ?? '600', 10);

  if (!templateId) {
    console.error(
      'Error: template ID required. Usage: canary sandbox warm <template-id> [--wait] [--timeout <sec>]'
    );
    console.error('Find the template id with: canary sandbox list');
    process.exit(1);
  }

  const ensure = (): Promise<{ ok: boolean; error?: string; data?: AuthoringInstance }> =>
    apiRequest<{ ok: boolean; error?: string; data?: AuthoringInstance }>(
      apiUrl,
      token,
      'POST',
      `/org/environments/templates/${templateId}/authoring-instance`
    );

  const first = await ensure();
  if (!first.ok || !first.data) {
    if (json) {
      console.log(JSON.stringify(first, null, 2));
    } else {
      console.error(`Error: ${first.error ?? 'Failed to warm sandbox'}`);
    }
    process.exit(1);
  }

  const terminalOk = new Set(['running']);
  const terminalFail = new Set(['failed', 'terminated', 'stopping']);

  let current = first.data;
  if (wait && !terminalOk.has(current.status) && !terminalFail.has(current.status)) {
    const deadline = Date.now() + timeoutSec * 1000;
    if (!json) console.log(`Warming flow-authoring sandbox (${current.status})...`);
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 5000));
      const poll = await ensure();
      if (!poll.ok || !poll.data) {
        console.error(`Error polling sandbox: ${poll.error ?? 'Unknown error'}`);
        process.exit(1);
      }
      current = poll.data;
      if (terminalOk.has(current.status) || terminalFail.has(current.status)) break;
    }
  }

  if (json) {
    console.log(JSON.stringify({ ok: true, data: current }, null, 2));
  } else {
    console.log(`Sandbox ${current.created ? 'created' : 'reused'}: ${current.instanceId}`);
    console.log(`  Status: ${current.status}`);
    if (current.url) console.log(`  URL: ${current.url}`);
    if (current.expiresAt) console.log(`  Expires: ${formatDate(current.expiresAt)}`);
    if (current.status === 'running') {
      console.log('\nReady. Run a workflow against it with:');
      console.log('  canary workflow run <workflowId> [--environment <name|id>] --stream');
    } else if (wait && terminalFail.has(current.status)) {
      console.log('\nSandbox did not reach a ready state.');
    } else if (wait) {
      console.log('\nTimed out waiting for the sandbox to become ready. Re-run to keep waiting.');
    } else {
      console.log('\nWarming in the background. Poll with: canary sandbox warm ' + templateId + ' --wait');
    }
  }

  if (terminalFail.has(current.status)) process.exit(1);
  if (wait && current.status !== 'running') process.exit(1);
}

export interface ContainerLine {
  name: string;
  status: string;
  state: 'up-healthy' | 'up' | 'up-unhealthy' | 'restarting' | 'exited' | 'other';
}

/**
 * Parse `docker ps --format '{{.Names}}\t{{.Status}}\t{{.Ports}}'` output
 * into a structured list. State classification is coarse — the verify loop
 * only needs to know "is this container healthy / exited / restart-looping".
 */
export function parseDockerPs(stdout: string): ContainerLine[] {
  const lines = stdout.split('\n').map((l) => l.trim()).filter(Boolean);
  return lines.map((line) => {
    const [name = '', status = ''] = line.split('\t');
    const lower = status.toLowerCase();
    let state: ContainerLine['state'] = 'other';
    if (lower.startsWith('restarting')) state = 'restarting';
    else if (lower.startsWith('exited')) state = 'exited';
    else if (lower.startsWith('up') && lower.includes('unhealthy')) state = 'up-unhealthy';
    else if (lower.startsWith('up') && lower.includes('healthy')) state = 'up-healthy';
    else if (lower.startsWith('up')) state = 'up';
    return { name, status, state };
  });
}

/** Containers that aren't cleanly up. Used to decide what to dump logs for. */
export function isBrokenContainer(c: ContainerLine): boolean {
  return c.state === 'restarting' || c.state === 'exited' || c.state === 'up-unhealthy';
}

/**
 * Map a container name back to its compose service.
 *
 * docker-compose names containers `<project>-<service>-<replica>`. The
 * sandbox platform sets the project to `workspace` via
 * `--project-directory /workspace` (see `SANDBOX_COMPOSE_BASE` in
 * apps/api/.../sandbox-cli/service.ts), so the common case is
 * `workspace-<svc>-<n>`. Fall back to stripping the trailing replica
 * index only, so attribution still works if the project name ever
 * changes or someone uses an explicit `container_name:` override.
 */
export function containerNameToService(containerName: string): string {
  const withProject = containerName.match(/^workspace-(.+)-\d+$/);
  if (withProject) return withProject[1]!;
  const withoutProject = containerName.match(/^(.+)-\d+$/);
  if (withoutProject) return withoutProject[1]!;
  return containerName;
}

async function fetchServiceLogs(
  apiUrl: string,
  token: string,
  instanceId: string,
  service: string,
  tail: number
): Promise<string> {
  const res = await apiRequest<{ ok: boolean; error?: string; data: { logs: string } }>(
    apiUrl,
    token,
    'POST',
    `/org/environments/instances/${instanceId}/docker/logs`,
    { service, tail }
  );
  if (!res.ok) return `(log fetch failed: ${res.error ?? 'unknown'})`;
  return res.data?.logs ?? '';
}

async function handleVerify(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const json = hasFlag(argv, '--json');
  // --keep-instance is kept as a success-path alias (existing behavior).
  // On failure the probe is now preserved by default so the user can debug;
  // pass --auto-terminate-on-failure for CI where you want cleanup anyway.
  const keepInstance = hasFlag(argv, '--keep-instance');
  const autoTerminateOnFailure = hasFlag(argv, '--auto-terminate-on-failure');

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox verify <template-id>');
    process.exit(1);
  }

  console.log(`Verifying template ${templateId}...`);

  // Kick off a verification instance — server bypasses the "verified" gate
  // via verificationMode but otherwise runs the full provisioning flow.
  const verifyRes = await apiRequest<{ ok: boolean; error?: string; data: { instanceId: string; expiresAt: string } }>(
    apiUrl, token, 'POST', `/org/environments/templates/${templateId}/verify`, {}
  );
  if (!verifyRes.ok) {
    console.error(`Error: ${verifyRes.error}`);
    process.exit(1);
  }
  const instanceId = verifyRes.data.instanceId;
  console.log(`  Verification instance: ${instanceId}`);

  // Poll diagnostics until instance reaches 'running' or a terminal state.
  const POLL_INTERVAL_MS = 10_000;
  const TIMEOUT_MS = 600_000; // 10 minutes
  const DETAIL_DUMP_INTERVAL_MS = 30_000; // rate-limit noisy dumps
  const LOG_TAIL_LINES = 40;
  const startTime = Date.now();
  let finalStatus = '';
  let publicUrl: string | undefined;
  let lastDetailDumpAt = 0;
  let routes: ServiceRoute[] = [];
  let routesFetched = false;

  while (Date.now() - startTime < TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const diag = await apiRequest<{ ok: boolean; error?: string; data: DiagnosticsResult }>(
      apiUrl, token, 'GET', `/org/environments/instances/${instanceId}/diagnostics`,
      undefined,
      { timeoutMs: 60_000 }
    );
    if (!diag.ok) continue;

    const now = Date.now();
    const elapsed = Math.round((now - startTime) / 1000);
    const remaining = Math.max(0, Math.round((TIMEOUT_MS - (now - startTime)) / 1000));
    const status = String((diag.data.instance as { status?: string; publicUrl?: string }).status ?? '');
    publicUrl = (diag.data.instance as { publicUrl?: string }).publicUrl;
    const appOutput = 'stdout' in diag.data.appHealth ? diag.data.appHealth.stdout : '';
    const appUp = appOutput.length > 0 && !appOutput.includes('UNREACHABLE');
    const dockerOutput = 'stdout' in diag.data.docker ? diag.data.docker.stdout : '';
    const containers = parseDockerPs(dockerOutput);
    const broken = containers.filter(isBrokenContainer);

    // Compact progress line (overwriting, like before) — adds remaining time.
    const ttl = remaining > 60 ? `${elapsed}s elapsed` : `${remaining}s remaining`;
    process.stdout.write(
      `\r  [${ttl}] status: ${status}, app: ${appUp ? 'up' : 'waiting'}, containers: ${containers.length}, unhealthy: ${broken.length}        `
    );

    if (status === 'running' && appUp) {
      process.stdout.write('\n');
      finalStatus = 'running';
      break;
    }
    if (status === 'failed' || status === 'terminated') {
      process.stdout.write('\n');
      finalStatus = status;
      break;
    }

    // When the app is still down and at least one container is broken,
    // dump details + log tails once every DETAIL_DUMP_INTERVAL_MS so the
    // user actually sees what's failing instead of staring at a spinner.
    const shouldDump =
      !appUp &&
      broken.length > 0 &&
      now - lastDetailDumpAt >= DETAIL_DUMP_INTERVAL_MS;
    if (shouldDump) {
      lastDetailDumpAt = now;
      process.stdout.write('\n');
      console.log('');
      console.log(`  App unreachable after ${elapsed}s. Container state:`);
      for (const c of containers) {
        console.log(`    ${c.name.padEnd(40)}  ${c.status}`);
      }
      console.log('');
      // Per-broken-container log tails. Cap to MAX_LOG_CONTAINERS to avoid
      // SSM call amplification on pathological cases (a template with 6
      // restart-looping services would otherwise issue 6 sequential SSM
      // calls every 30s). Fetch in parallel — these are independent.
      const MAX_LOG_CONTAINERS = 3;
      const toFetch = broken.slice(0, MAX_LOG_CONTAINERS);
      const overflow = broken.length - toFetch.length;
      const fetched = await Promise.all(
        toFetch.map((c) =>
          fetchServiceLogs(apiUrl, token, instanceId, containerNameToService(c.name), LOG_TAIL_LINES)
            .then((logs) => ({ c, logs }))
        )
      );
      for (const { c, logs } of fetched) {
        const tail = logs.trim().split('\n').slice(-LOG_TAIL_LINES).join('\n    ');
        console.log(`  --- ${c.name} (${c.state}) ---`);
        console.log(`    ${tail || '(no logs)'}`);
        console.log('');
      }
      if (overflow > 0) {
        console.log(
          `  (+${overflow} more broken container${overflow === 1 ? '' : 's'} — re-run with: canary sandbox logs ${instanceId} --service <name>)`
        );
        console.log('');
      }
      // Attribution: if nginx-proxy is looping because of a labeled upstream
      // that's exited, call that out so the user doesn't chase nginx's
      // "host not found in upstream" as the root cause.
      if (!routesFetched) {
        routes = await fetchInstanceRoutes(apiUrl, token, instanceId);
        routesFetched = true;
      }
      const nginxProxy = containers.find((c) => containerNameToService(c.name) === 'nginx-proxy');
      if (nginxProxy?.state === 'restarting' && routes.length > 0) {
        const failedUpstreams = routes
          .map((r) => ({
            route: r.route,
            service: r.service,
            container: broken.find((c) => containerNameToService(c.name) === r.service),
          }))
          .filter((x) => x.container);
        if (failedUpstreams.length > 0) {
          for (const fu of failedUpstreams) {
            console.log(
              `  >> nginx-proxy is restart-looping because upstream '${fu.service}' (route '${fu.route}') is ${fu.container!.state}. Read that service's logs above, not nginx's.`
            );
          }
          console.log('');
        }
      }
    }
  }

  if (finalStatus !== 'running') {
    console.error('');
    console.error(`Verification failed — instance ended in status '${finalStatus || 'timeout'}'.`);
    if (publicUrl) console.error(`  Instance URL: ${publicUrl}`);
    console.error(`  Instance ID:  ${instanceId}`);
    console.error(`  Debug with:   canary sandbox diagnostics ${instanceId}`);
    console.error(`                canary sandbox logs ${instanceId} --service <name>`);
    console.error(`                canary sandbox run-command ${instanceId} 'docker ps -a'`);
    if (autoTerminateOnFailure) {
      await apiRequest(apiUrl, token, 'POST', `/org/environments/instances/${instanceId}/terminate`);
      console.error(`  Terminated (--auto-terminate-on-failure).`);
    } else {
      // Default flip (was: auto-terminate). Call it out so CI users don't
      // silently accumulate orphan instances.
      console.error(`  Instance preserved by default for debugging. Pass --auto-terminate-on-failure in CI.`);
      console.error(`  Terminate manually with:`);
      console.error(`                canary sandbox terminate ${instanceId}`);
    }
    process.exit(1);
  }

  // We just observed the instance reach status === 'running' && appUp.
  // That's the same observation that backs the server-running and
  // server-accessible checklist items, so flip them too — best-effort
  // (templates without those items in their checklist 404 silently).
  const evidenceSummary = `Verified via canary sandbox verify — fresh instance ${instanceId} came up healthy.`;
  for (const itemId of ['server-running', 'server-accessible']) {
    await apiRequest(
      apiUrl,
      token,
      'POST',
      `/org/environments/templates/${templateId}/checklist/${itemId}/complete`,
      { status: 'completed', summary: evidenceSummary }
    ).catch(() => {
      // Item not present on this checklist profile — fine.
    });
  }

  // Mark the template-verified checklist item complete — this is what flips
  // template.status to 'verified' (via ChecklistService.completeChecklistItem).
  const completeRes = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/environments/templates/${templateId}/checklist/template-verified/complete`,
    {
      status: 'completed',
      summary: evidenceSummary,
    }
  );
  if (!completeRes.ok) {
    console.error(`Failed to record verification: ${completeRes.error}`);
    console.error(`Instance ${instanceId} left running for inspection.`);
    process.exit(1);
  }

  if (!keepInstance) {
    await apiRequest(apiUrl, token, 'POST', `/org/environments/instances/${instanceId}/terminate`);
    console.log(`  Terminated verification instance.`);
  } else {
    console.log(`  --keep-instance: instance ${instanceId} left running${publicUrl ? ` at ${publicUrl}` : ''}.`);
  }

  // Confirm the template actually flipped to verified. The status flip only
  // happens when every item in the checklist profile is terminal
  // (completed or skipped); if other items are still pending, the probe
  // succeeded but the template stays in its prior status.
  const templateAfter = await apiRequest<{ ok: boolean; data?: { status?: string } }>(
    apiUrl,
    token,
    'GET',
    `/org/environments/templates/${templateId}`
  ).catch(() => ({ ok: false }) as { ok: boolean; data?: { status?: string } });
  const templateStatus = templateAfter.data?.status;
  const verified = templateStatus === 'verified';

  if (json) {
    console.log(JSON.stringify({ templateId, verified, status: templateStatus, instanceId, publicUrl }, null, 2));
    return;
  }

  if (verified) {
    console.log('Template verified.');
    return;
  }

  // Probe passed but status didn't flip — surface the pending items so the
  // user knows what to complete/skip before re-running verify.
  console.log(`Verification probe passed (instance ${instanceId} came up healthy),`);
  console.log(`but template status is still '${templateStatus ?? 'unknown'}'.`);
  type ChecklistItemSummary = { id: string; label: string; itemStatus?: { status?: string } };
  const checklistRes = await apiRequest<{ ok: boolean; data?: ChecklistItemSummary[] }>(
    apiUrl,
    token,
    'GET',
    `/org/environments/templates/${templateId}/checklist`
  ).catch(() => ({ ok: false }) as { ok: boolean; data?: ChecklistItemSummary[] });
  const pending = (checklistRes.data ?? []).filter((it) => {
    const s = it.itemStatus?.status ?? 'pending';
    return s !== 'completed' && s !== 'skipped';
  });
  if (pending.length > 0) {
    console.log('Pending checklist items must be completed or skipped:');
    for (const item of pending) {
      console.log(`  - ${item.label} (${item.id})`);
    }
    console.log('');
    console.log('Complete:  canary sandbox checklist complete <template-id> <item-id> --status success --summary "..."');
    console.log('Skip:      canary sandbox checklist skip     <template-id> <item-id>');
  }
  process.exit(1);
}

async function handleArtifactGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const type = positional[1];
  const outputFile = getArgValue(argv, '--output');

  if (!templateId || !type) {
    console.error('Error: template ID and artifact type required. Usage: canary sandbox artifact get <template-id> <type>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: Artifact }>(
    apiUrl, token, 'GET', `/org/environments/templates/${templateId}/artifacts/${type}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (outputFile) {
    const { writeFileSync } = await import('node:fs');
    writeFileSync(outputFile, res.data.content ?? '', 'utf-8');
    console.log(`Written to ${outputFile}`);
  } else {
    process.stdout.write(res.data.content ?? '');
  }
}

async function handleArtifactCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const type = positional[1];
  const filePath = positional[2];
  const json = hasFlag(argv, '--json');

  if (!templateId || !type || !filePath) {
    console.error('Error: all args required. Usage: canary sandbox artifact create <template-id> <type> <file>');
    process.exit(1);
  }

  // Keep in sync with WellKnownArtifactType in packages/types/src/sandbox-tasks.ts + 'file' (custom with --path)
  const WELL_KNOWN_TYPES = ['docker-compose', 'dockerfile', 'startup-script', 'setup-script', 'seed-script', 'environment-config', 'file'];
  if (!WELL_KNOWN_TYPES.includes(type)) {
    console.error(`Error: invalid artifact type "${type}". Valid types: ${WELL_KNOWN_TYPES.join(', ')}`);
    process.exit(1);
  }

  // File artifacts require --path <destination>
  const destinationPath = getArgValue(argv, '--path');
  if (type === 'file' && !destinationPath) {
    console.error('Error: file artifacts require --path <destination>. Example: --path /workspace/.canary/vite.sandbox.config.ts');
    process.exit(1);
  }
  if (destinationPath && !destinationPath.startsWith('/workspace/.canary/')) {
    const suggestedName = destinationPath.split('/').filter(Boolean).pop() ?? 'yourfile';
    console.error(
      'Error: --path must be under /workspace/.canary/ (the repo tree is user-owned — sandbox files live in .canary/).\n' +
      `       try: --path /workspace/.canary/${suggestedName}`
    );
    process.exit(1);
  }

  let content: string;
  try {
    content = readFileSync(filePath, 'utf-8');
  } catch (err) {
    console.error(`Error reading file: ${err}`);
    process.exit(1);
  }

  // Derive filename from path
  const filename = filePath.split('/').pop() ?? filePath;

  // Artifact upload writes through to the live authoring instance, which on
  // first push (or a large file) can outlast 30s easily. 90s lines up with
  // the slowest legitimate upload we've observed.
  const res = await apiRequest<{
    ok: boolean;
    error?: string;
    data?: { kind: 'created' | 'replaced'; previousPath?: string };
  }>(
    apiUrl, token, 'PUT', `/org/environments/templates/${templateId}/artifacts`, {
      artifactType: type,
      filename,
      content,
      ...(destinationPath ? { destinationPath } : {}),
    },
    { timeoutMs: 90_000 }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const kind = res.data?.kind;
  const marker = kind === 'replaced' ? ' (replaced)' : kind === 'created' ? ' (created)' : '';
  console.log(`Artifact uploaded: ${type} (${filename}, ${content.length} bytes)${marker}`);
  if (kind === 'replaced' && res.data?.previousPath) {
    console.log(`  previous destination removed: ${res.data.previousPath}`);
  }
}

async function handleBuild(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const json = hasFlag(argv, '--json');
  const noWait = hasFlag(argv, '--no-wait');
  const instanceId = positional[0];

  if (!instanceId) {
    console.error('Error: instance ID required. Usage: canary sandbox build <instance-id>');
    process.exit(1);
  }

  console.log('Building sandbox (this takes a few minutes)...');

  // The build is async by design: a single HTTP connection cannot reliably
  // span a multi-minute build (ALB caps, our own client timeouts, flaky
  // networks). Kick the build off with a short timeout — the server has
  // already started the work by the time it would otherwise reply — then
  // move to polling diagnostics for the truth.
  const KICKOFF_TIMEOUT_MS = 8_000;

  let buildCompleted = false;
  let buildResult: { stdout: string; stderr: string; exitCode: number } | null = null;

  const result = await apiFetch(`${apiUrl}/org/environments/instances/${instanceId}/build`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    token,
    timeoutMs: KICKOFF_TIMEOUT_MS,
  });

  if (result.ok) {
    // Fast path: server responded within the kickoff window (cached/no-op build).
    const data = (await result.response.json().catch(() => null)) as
      | { ok: boolean; error?: string; data?: { stdout: string; stderr: string; exitCode: number } }
      | null;
    if (data?.ok && data.data) {
      buildCompleted = true;
      buildResult = data.data;
    } else if (data && !data.ok) {
      console.error(`Error: ${data.error ?? 'build failed'}`);
      process.exit(1);
    }
  } else {
    const { failure } = result;
    // network = our kickoff timeout firing; 502/504 = gateway giving up while
    // the build runs on. Both mean "watch via polling, not an error".
    const isExpectedAsync =
      failure.kind === 'network' ||
      (failure.kind === 'http-error' && (failure.status === 502 || failure.status === 504));
    if (!isExpectedAsync) {
      if (isTerminalFailure(failure)) exitOnFailure(failure);
      console.error(`Error: ${failure.message}`);
      process.exit(1);
    }
  }

  // If build completed synchronously, print results and exit
  if (buildCompleted && buildResult) {
    if (json) {
      console.log(JSON.stringify(buildResult, null, 2));
      return;
    }

    if (buildResult.stdout) {
      process.stdout.write(buildResult.stdout);
      if (!buildResult.stdout.endsWith('\n')) process.stdout.write('\n');
    }
    if (buildResult.stderr) {
      process.stderr.write(`STDERR: ${buildResult.stderr}`);
      if (!buildResult.stderr.endsWith('\n')) process.stderr.write('\n');
    }

    if (buildResult.exitCode === 0) {
      console.log('Build complete.');
    } else {
      console.error(`Build failed with exit code ${buildResult.exitCode}`);
      process.exit(1);
    }
    return;
  }

  // Build is async — poll diagnostics until the app is reachable or timeout
  if (noWait) {
    console.log('Build started. Use diagnostics to monitor progress:');
    console.log(`  canary sandbox diagnostics ${instanceId}`);
    return;
  }

  console.log('Build started, watching for services...');

  const BUILD_POLL_TIMEOUT_MS = 300_000; // 5 minutes
  const POLL_INTERVAL_MS = 10_000;
  const startTime = Date.now();

  while (Date.now() - startTime < BUILD_POLL_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const diag = await apiRequest<{ ok: boolean; error?: string; data: DiagnosticsResult }>(
      apiUrl, token, 'GET', `/org/environments/instances/${instanceId}/diagnostics`,
      undefined,
      { timeoutMs: 60_000 }
    );

    if (!diag.ok) continue;

    const elapsed = Math.round((Date.now() - startTime) / 1000);
    const dockerOutput = 'stdout' in diag.data.docker ? diag.data.docker.stdout : '';
    const appOutput = 'stdout' in diag.data.appHealth ? diag.data.appHealth.stdout : '';
    const appUp = appOutput.length > 0 && !appOutput.includes('UNREACHABLE');

    // Show a compact progress line
    const containerCount = (dockerOutput.match(/\n/g) || []).length + (dockerOutput ? 1 : 0);
    const healthyCount = (dockerOutput.match(/\(healthy\)/g) || []).length;
    process.stdout.write(`\r  [${elapsed}s] containers: ${containerCount}, healthy: ${healthyCount}, app: ${appUp ? 'up' : 'waiting'}  `);

    // Trust the HTTP probe: if nginx is serving the app on :3000 or :8000, we're done.
    // Compose healthchecks are optional and most user services don't declare them.
    if (appUp) {
      process.stdout.write('\n');
      console.log('Build complete.');
      return;
    }
  }

  process.stdout.write('\n');
  console.error('Build timed out waiting for the app to respond. Check diagnostics and logs:');
  console.error(`  canary sandbox diagnostics ${instanceId}`);
  console.error(`  canary sandbox logs ${instanceId} --service <name>`);
  process.exit(1);
}

async function handleRunCommand(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const service = getArgValue(argv, '--service');
  const timeoutFlag = getArgValue(argv, '--timeout');

  // Strip our flags (--json, --service <value>, --timeout <value>) so they
  // don't leak into the remote command.
  const filtered = argv.filter((a, i, arr) => {
    if (a === '--json') return false;
    if (a === '--service' || a === '--timeout') return false;
    if (i > 0 && (arr[i - 1] === '--service' || arr[i - 1] === '--timeout')) return false;
    return true;
  });

  // First non-flag arg is the instance ID; everything after it is the command
  // (including flags like -la that belong to the remote command, not to us)
  const firstNonFlag = filtered.findIndex((a) => !a.startsWith('-'));
  const instanceId = firstNonFlag >= 0 ? filtered[firstNonFlag] : undefined;
  const command = firstNonFlag >= 0 ? filtered.slice(firstNonFlag + 1).join(' ') : '';

  if (!instanceId || !command) {
    console.error('Error: instance ID and command required. Usage: canary sandbox run-command <instance-id> [--service <name>] [--timeout <ms>] <command...>');
    process.exit(1);
  }

  // Server-side run-command caps at 55s (SANDBOX_COMMAND_TIMEOUT). Default
  // the CLI just past that so we surface the server's typed timeout rather
  // than an opaque client-side cancel. For longer ops (test suites,
  // playwright), pass a higher value or use first-class actions.
  const timeoutMs = timeoutFlag ? Math.max(parseInt(timeoutFlag, 10), 1000) : 60_000;

  const res = await apiRequest<{ ok: boolean; error?: string; data: { stdout: string; stderr: string; exitCode: number } }>(
    apiUrl, token, 'POST', `/org/environments/instances/${instanceId}/run-command`,
    { command, ...(service ? { service } : {}) },
    { timeoutMs }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  if (res.data.stdout) {
    process.stdout.write(res.data.stdout);
    if (!res.data.stdout.endsWith('\n')) process.stdout.write('\n');
  }
  if (res.data.stderr) {
    process.stderr.write(`STDERR: ${res.data.stderr}`);
    if (!res.data.stderr.endsWith('\n')) process.stderr.write('\n');
  }
  if (res.data.exitCode !== 0) {
    console.log(`Exit code: ${res.data.exitCode}`);
  }
}

async function handleDiagnostics(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!instanceId) {
    console.error('Error: instance ID required. Usage: canary sandbox diagnostics <instance-id>');
    process.exit(1);
  }

  // Server-side DIAGNOSTICS_TIMEOUT_MS is 30s; the client needs a bit more
  // headroom than that or we race the server and report a misleading client
  // timeout for a server-side timeout.
  const res = await apiRequest<{ ok: boolean; error?: string; data: DiagnosticsResult }>(
    apiUrl, token, 'GET', `/org/environments/instances/${instanceId}/diagnostics`,
    undefined,
    { timeoutMs: 60_000 }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  const routes = await fetchInstanceRoutes(apiUrl, token, instanceId);
  const data = { ...res.data, routes };

  if (json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const d = data;

  console.log('=== Instance ===');
  for (const [key, value] of Object.entries(d.instance)) {
    console.log(`  ${key}: ${value}`);
  }

  if (d.lifecycle) {
    console.log('');
    console.log('=== Lifecycle ===');
    console.log(`  databaseStatus: ${d.lifecycle.databaseStatus}`);
    console.log(`  poolStatus: ${d.lifecycle.poolStatus ?? 'missing'}`);
    console.log(`  ec2InstanceState: ${d.lifecycle.ec2InstanceState ?? 'unknown'}`);
    console.log(`  live: ${d.lifecycle.live}`);
    if (d.lifecycle.message) console.log(`  message: ${d.lifecycle.message}`);
  }

  if (d.provision) {
    console.log('');
    console.log('=== Provisioning ===');
    console.log(`  phase: ${d.provision.phase}`);
    console.log(`  jobStatus: ${d.provision.jobStatus}`);
    if (d.provision.startedAt) console.log(`  startedAt: ${d.provision.startedAt}`);
    if (d.provision.elapsedSeconds !== null) console.log(`  elapsed: ${d.provision.elapsedSeconds}s`);
    if (d.provision.lastError) console.log(`  lastError: ${d.provision.lastError}`);
  }

  const sections: Array<[string, CommandOrError]> = [
    ['Docker Containers', d.docker],
    ['Git Status', d.git],
    ['Disk Usage', d.disk],
    ['Agent Health', d.agentHealth],
    ['App Health', d.appHealth],
  ];
  if (d.resources) sections.splice(3, 0, ['Resource Usage', d.resources]);

  for (const [title, result] of sections) {
    console.log('');
    console.log(`=== ${title} ===`);
    if ('error' in result) {
      console.log(`  Error${result.code ? ` [${result.code}]` : ''}: ${result.error}`);
    } else {
      if (result.stdout) console.log(result.stdout);
      if (result.stderr) console.log(`  STDERR: ${result.stderr}`);
    }
  }
}

async function handleTerminate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];

  if (!instanceId) {
    console.error('Error: instance ID required. Usage: canary sandbox terminate <instance-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl, token, 'POST', `/org/environments/instances/${instanceId}/terminate`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Instance ${instanceId} termination initiated.`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox delete <template-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl, token, 'DELETE', `/org/environments/templates/${templateId}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Template ${templateId} deleted.`);
}

// ---------------------------------------------------------------------------
// Action handlers
// ---------------------------------------------------------------------------

type ActionDef = {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  icon: string | null;
  enabled: boolean;
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown> | null;
  /** Declared handover slots — what this action hands back, and of what kind. */
  outputConfig?: Array<Record<string, unknown>> | null;
  scriptContent: string;
  timeoutMs: number;
  createdAt: string;
  updatedAt: string;
};

type ActionExecution = {
  id: string;
  actionId: string;
  instanceId: string;
  source: string;
  status: string;
  result: Record<string, unknown> | null;
  stdout: string | null;
  stderr: string | null;
  exitCode: number | null;
  durationMs: number | null;
  error: string | null;
  createdAt: string;
};

async function handleActionList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!templateId) {
    console.error('Error: template ID required. Usage: canary sandbox action list <template-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: ActionDef[] }>(
    apiUrl, token, 'GET', `/org/environments/templates/${templateId}/actions`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const actions = res.data ?? [];
  if (actions.length === 0) {
    console.log('No actions defined for this template.');
    return;
  }

  console.log(`Actions (${actions.length}):`);
  for (const a of actions) {
    const status = a.enabled ? '' : ' [disabled]';
    console.log(`  ${pad(a.slug, 30)} ${pad(a.label, 25)}${status}`);
  }
}

async function handleActionGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const slug = positional[1];
  const json = hasFlag(argv, '--json');

  if (!templateId || !slug) {
    console.error('Error: template ID and slug required. Usage: canary sandbox action get <template-id> <slug>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: ActionDef }>(
    apiUrl, token, 'GET', `/org/environments/templates/${templateId}/actions/${slug}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const a = res.data;
  console.log(`Action: ${a.slug}`);
  console.log(`  Label:       ${a.label}`);
  console.log(`  Description: ${a.description ?? '(none)'}`);
  console.log(`  Icon:        ${a.icon ?? '(none)'}`);
  console.log(`  Enabled:     ${a.enabled}`);
  console.log(`  Timeout:     ${a.timeoutMs}ms`);
  console.log(`  Input Schema:`);
  console.log(`    ${JSON.stringify(a.inputSchema, null, 2).split('\n').join('\n    ')}`);
  if (a.outputSchema) {
    console.log(`  Output Schema:`);
    console.log(`    ${JSON.stringify(a.outputSchema, null, 2).split('\n').join('\n    ')}`);
  }
  // Printed unconditionally when present: "did the handover slot actually save"
  // is the first thing to check when a run reports no results.
  if (a.outputConfig && a.outputConfig.length > 0) {
    console.log(`  Output Config (handover slots):`);
    console.log(`    ${JSON.stringify(a.outputConfig, null, 2).split('\n').join('\n    ')}`);
  }
  console.log(`  Script:`);
  console.log(`    ${a.scriptContent.split('\n').join('\n    ')}`);
}

async function handleActionCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const slug = positional[1];
  const json = hasFlag(argv, '--json');

  if (!templateId || !slug) {
    console.error('Error: template ID and slug required. Usage: canary sandbox action create <template-id> <slug> [options]');
    process.exit(1);
  }

  const scriptPath = getArgValue(argv, '--script');
  const inputSchemaPath = getArgValue(argv, '--input-schema');
  const outputSchemaPath = getArgValue(argv, '--output-schema');
  const credentialOutputConfigPath = getArgValue(argv, '--credential-output-config');
  const outputConfigPath = getArgValue(argv, '--output-config');
  const label = getArgValue(argv, '--label') ?? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const description = getArgValue(argv, '--description');
  const icon = getArgValue(argv, '--icon');
  const timeoutStr = getArgValue(argv, '--timeout');
  const encryptScript = hasFlag(argv, '--encrypt-script');
  const credentialTemplateId = getArgValue(argv, '--credential-template');
  const provisioningMode = getArgValue(argv, '--provisioning-mode');
  const runOnProvision = hasFlag(argv, '--run-on-provision');
  const provisionOrderStr = getArgValue(argv, '--provision-order');

  if (provisioningMode && provisioningMode !== 'shared' && provisioningMode !== 'unique') {
    console.error("Error: --provisioning-mode must be 'shared' or 'unique'");
    process.exit(1);
  }

  if (provisionOrderStr && !/^-?\d+$/.test(provisionOrderStr)) {
    console.error('Error: --provision-order must be an integer');
    process.exit(1);
  }

  if (!scriptPath) {
    console.error('Error: --script <file> is required');
    process.exit(1);
  }
  if (!inputSchemaPath) {
    console.error('Error: --input-schema <file> is required');
    process.exit(1);
  }

  let scriptContent: string;
  try {
    scriptContent = readFileSync(scriptPath, 'utf-8');
  } catch (err) {
    console.error(`Error reading script file: ${err}`);
    process.exit(1);
  }

  let inputSchema: Record<string, unknown>;
  try {
    inputSchema = JSON.parse(readFileSync(inputSchemaPath, 'utf-8'));
  } catch (err) {
    console.error(`Error reading input schema file: ${err}`);
    process.exit(1);
  }

  let outputSchema: Record<string, unknown> | undefined;
  if (outputSchemaPath) {
    try {
      outputSchema = JSON.parse(readFileSync(outputSchemaPath, 'utf-8'));
    } catch (err) {
      console.error(`Error reading output schema file: ${err}`);
      process.exit(1);
    }
  }

  let credentialOutputConfig: Record<string, unknown> | undefined;
  if (credentialOutputConfigPath) {
    try {
      credentialOutputConfig = JSON.parse(readFileSync(credentialOutputConfigPath, 'utf-8'));
    } catch (err) {
      console.error(`Error reading credential output config file: ${err}`);
      process.exit(1);
    }
  }

  // The declared handover: which files this action leaves behind and what kind
  // each one is. Without this flag the column is only reachable through a raw
  // API call, which would make a shipped feature unusable from the tools an
  // operator actually has. Validated server-side against the output-kind
  // registry, so a bad slot is rejected at save time rather than mid-run.
  let outputConfig: unknown;
  if (outputConfigPath) {
    try {
      outputConfig = JSON.parse(readFileSync(outputConfigPath, 'utf-8'));
    } catch (err) {
      console.error(`Error reading output config file: ${err}`);
      process.exit(1);
    }
    if (!Array.isArray(outputConfig)) {
      console.error('Error: --output-config must contain a JSON array of output slots');
      process.exit(1);
    }
  }

  const body: Record<string, unknown> = {
    label,
    scriptContent,
    inputSchema,
    ...(description ? { description } : {}),
    ...(icon ? { icon } : {}),
    ...(outputSchema ? { outputSchema } : {}),
    ...(credentialOutputConfig ? { credentialOutputConfig } : {}),
    ...(outputConfig ? { outputConfig } : {}),
    ...(credentialTemplateId ? { credentialTemplateId } : {}),
    ...(provisioningMode ? { provisioningMode } : {}),
    ...(runOnProvision ? { runOnProvision: true } : {}),
    ...(provisionOrderStr ? { provisionOrder: parseInt(provisionOrderStr, 10) } : {}),
    ...(timeoutStr ? { timeoutMs: parseInt(timeoutStr, 10) } : {}),
    ...(encryptScript ? { scriptEncrypted: true } : {}),
  };

  const res = await apiRequest<{ ok: boolean; error?: string; data?: ActionDef }>(
    apiUrl, token, 'PUT', `/org/environments/templates/${templateId}/actions/${slug}`, body
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  console.log(`Action '${slug}' saved (${scriptContent.length} bytes script, label: "${label}")`);
}

async function handleActionDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const templateId = positional[0];
  const slug = positional[1];

  if (!templateId || !slug) {
    console.error('Error: template ID and slug required. Usage: canary sandbox action delete <template-id> <slug>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl, token, 'DELETE', `/org/environments/templates/${templateId}/actions/${slug}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  console.log(`Action '${slug}' deleted.`);
}

async function handleActionExecute(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];
  const slug = positional[1];
  const json = hasFlag(argv, '--json');

  if (!instanceId || !slug) {
    console.error('Error: instance ID and slug required. Usage: canary sandbox action execute <instance-id> <slug> [options]');
    process.exit(1);
  }

  // Collect params from --param key=value flags
  let params: Record<string, unknown> = {};

  const paramsFile = getArgValue(argv, '--params-file');
  if (paramsFile) {
    try {
      params = JSON.parse(readFileSync(paramsFile, 'utf-8'));
    } catch (err) {
      console.error(`Error reading params file: ${err}`);
      process.exit(1);
    }
  }

  // --param flags override params-file
  for (let i = 0; i < argv.length; i++) {
    if (argv[i] === '--param' && argv[i + 1]) {
      const [key, ...valueParts] = argv[i + 1]!.split('=');
      if (key && valueParts.length > 0) {
        params[key] = valueParts.join('=');
      }
      i++; // skip value
    }
  }

  interface ActionExecuteResponse {
    executionId: string;
    status: string;
    result: unknown;
    stdout: string | null;
    stderr: string | null;
    exitCode: number | null;
    durationMs: number | null;
    error: string | null;
    credentialId?: string | null;
  }

  const runAsync = hasFlag(argv, '--async');
  const timeoutArg = getArgValue(argv, '--timeout-ms');
  let executionTimeoutMs: number | undefined;
  if (timeoutArg) {
    const parsed = Number(timeoutArg);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      console.error(`Error: --timeout-ms must be a positive number, got '${timeoutArg}'`);
      process.exit(1);
    }
    if (!runAsync) {
      // The server ignores it without --async, so accepting it quietly would
      // leave the caller believing a long ceiling was applied when it was not.
      console.error('Error: --timeout-ms only applies with --async');
      process.exit(1);
    }
    executionTimeoutMs = parsed;
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data?: ActionExecuteResponse }>(
    apiUrl, token, 'POST', `/org/environments/instances/${instanceId}/actions/${slug}/execute`, {
      params,
      source: 'cli',
      ...(runAsync ? { async: true } : {}),
      ...(executionTimeoutMs !== undefined ? { executionTimeoutMs } : {}),
    }
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const exec = res.data!;

  // A detached run has no outcome yet, so the completion summary below would
  // print a misleading "exit ?" line. Tell the caller how to follow it instead.
  if (runAsync) {
    console.log(`[started] Action '${slug}' running in the background.`);
    console.log(`Execution: ${exec.executionId}`);
    console.log(
      `Follow:    cofactor sandbox action execution get ${instanceId} ${exec.executionId}`
    );
    console.log(
      `Cancel:    cofactor sandbox action execution cancel ${instanceId} ${exec.executionId}`
    );
    return;
  }
  const statusIcon = exec.status === 'completed' ? '[done]' : exec.status === 'failed' ? '[FAIL]' : `[${exec.status}]`;
  console.log(`${statusIcon} Action '${slug}' ${exec.status} (${exec.durationMs ?? 0}ms, exit ${exec.exitCode ?? '?'})`);
  if (exec.result) {
    console.log(`Result: ${JSON.stringify(exec.result, null, 2)}`);
  }
  if (exec.credentialId) {
    console.log(`Credential: ${exec.credentialId} (linked in property vault)`);
  }
  if (exec.status === 'failed' && exec.stderr) {
    // Cap by bytes first so a single multi-MB line doesn't blow up the terminal,
    // then by line count for readability.
    const tail = exec.stderr.slice(-10_000).trim().split('\n').slice(-20).join('\n');
    console.log(`Stderr (last 20 lines):\n${tail}`);
  }
  if (exec.error) {
    console.log(`Error: ${exec.error}`);
  }
}

async function handleActionExecutions(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];
  const json = hasFlag(argv, '--json');

  if (!instanceId) {
    console.error('Error: instance ID required. Usage: canary sandbox action executions <instance-id>');
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data: ActionExecution[] }>(
    apiUrl, token, 'GET', `/org/environments/instances/${instanceId}/actions/executions`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  const execs = res.data ?? [];
  if (execs.length === 0) {
    console.log('No executions yet.');
    return;
  }

  console.log('Executions:');
  for (const e of execs) {
    const icon = STATUS_ICONS[e.status] ?? `[${e.status}]`;
    const dur = e.durationMs !== null && e.durationMs !== undefined ? `${e.durationMs}ms` : '?ms';
    console.log(`  ${icon} ${pad(e.id.slice(0, 8), 10)} ${pad(e.source, 6)} ${pad(e.status, 12)} ${pad(dur, 8)} ${formatDate(e.createdAt)}`);
  }
}

/** Print one execution the same way `action execute` prints a finished run. */
function printExecutionDetail(exec: ActionExecution): void {
  const icon = STATUS_ICONS[exec.status] ?? `[${exec.status}]`;
  const dur = exec.durationMs !== null && exec.durationMs !== undefined ? `${exec.durationMs}ms` : '?ms';
  console.log(`${icon} ${exec.status} (${dur}, exit ${exec.exitCode ?? '?'})`);
  console.log(`Execution: ${exec.id}`);
  if (exec.result) {
    console.log(`Result: ${JSON.stringify(exec.result, null, 2)}`);
  }
  if (exec.stderr) {
    const tail = exec.stderr.slice(-10_000).trim().split('\n').slice(-20).join('\n');
    if (tail) console.log(`Stderr (last 20 lines):\n${tail}`);
  }
  if (exec.error) {
    console.log(`Error: ${exec.error}`);
  }
}

/**
 * Read one execution. For a detached run this is also what advances it: the
 * server reconciles a still-`running` row against SSM on read, so polling this
 * is how a background action reaches a terminal state.
 */
async function handleActionExecutionGet(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];
  const executionId = positional[1];
  const json = hasFlag(argv, '--json');

  if (!instanceId || !executionId) {
    console.error(
      'Error: instance ID and execution ID required. Usage: cofactor sandbox action execution get <instance-id> <execution-id>'
    );
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data?: ActionExecution }>(
    apiUrl,
    token,
    'GET',
    `/org/environments/instances/${instanceId}/actions/executions/${executionId}`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  printExecutionDetail(res.data!);
}

/** Stop a detached execution. Idempotent; a finished run is reported as-is. */
async function handleActionExecutionCancel(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  const instanceId = positional[0];
  const executionId = positional[1];
  const json = hasFlag(argv, '--json');

  if (!instanceId || !executionId) {
    console.error(
      'Error: instance ID and execution ID required. Usage: cofactor sandbox action execution cancel <instance-id> <execution-id>'
    );
    process.exit(1);
  }

  const res = await apiRequest<{ ok: boolean; error?: string; data?: ActionExecution }>(
    apiUrl,
    token,
    'POST',
    `/org/environments/instances/${instanceId}/actions/executions/${executionId}/cancel`
  );

  if (!res.ok) {
    console.error(`Error: ${res.error}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }

  printExecutionDetail(res.data!);
}

// ---------------------------------------------------------------------------
// Multi-repo: manage repos linked to a sandbox template
// ---------------------------------------------------------------------------

type TemplateRepoLink = {
  id: string;
  templateId: string;
  propertyGithubRepoId: string;
  role: string | null;
  branch: string;
  subdirectory: string | null;
  serviceName: string;
};

/**
 * Parse `role=owner/name@branch:subdir` (branch and subdir optional) into the
 * pieces the API expects. Used by `canary sandbox repo add --spec ...`.
 */
function parseRepoSpec(spec: string): {
  role?: string;
  repoFullName: string;
  branch?: string;
  subdirectory?: string;
} {
  let rest = spec;
  let role: string | undefined;
  const eq = rest.indexOf('=');
  if (eq > -1 && !rest.slice(0, eq).includes('/')) {
    role = rest.slice(0, eq);
    rest = rest.slice(eq + 1);
  }
  let subdirectory: string | undefined;
  const colon = rest.indexOf(':');
  if (colon > -1) {
    subdirectory = rest.slice(colon + 1);
    rest = rest.slice(0, colon);
  }
  let branch: string | undefined;
  const at = rest.indexOf('@');
  if (at > -1) {
    branch = rest.slice(at + 1);
    rest = rest.slice(0, at);
  }
  return { role, repoFullName: rest, branch, subdirectory };
}

async function handleRepoList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const templateId = getArgValue(argv, '--template');
  if (!templateId) {
    console.error('Error: --template is required.');
    process.exit(1);
  }

  const result = await apiRequest<{ ok: boolean; items?: TemplateRepoLink[]; error?: string }>(
    apiUrl,
    token,
    'GET',
    `/org/environments/templates/${templateId}/repos`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (json) {
    console.log(JSON.stringify(result.items ?? [], null, 2));
    return;
  }

  const items = result.items ?? [];
  if (items.length === 0) {
    console.log('No repos linked to this template.');
    return;
  }
  for (const r of items) {
    const role = r.role ? ` [${r.role}]` : '';
    const subdir = r.subdirectory ? `:${r.subdirectory}` : '';
    console.log(`  ${r.serviceName} → ${r.propertyGithubRepoId}@${r.branch}${subdir}${role}  (link=${r.id})`);
  }
}

async function handleRepoAdd(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const templateId = getArgValue(argv, '--template');
  const propertyGithubRepoId = getArgValue(argv, '--property-github-repo-id');
  const spec = getArgValue(argv, '--spec');
  const role = getArgValue(argv, '--role');
  const branch = getArgValue(argv, '--branch');
  const subdirectory = getArgValue(argv, '--subdirectory');
  const serviceName = getArgValue(argv, '--service-name');

  if (!templateId || !propertyGithubRepoId) {
    console.error('Error: --template and --property-github-repo-id are required.');
    console.error('Tip: link the repo to the property first via `canary property repo link`, then pass its id here.');
    process.exit(1);
  }

  const parsed: ReturnType<typeof parseRepoSpec> | { role?: undefined; branch?: undefined; subdirectory?: undefined } = spec ? parseRepoSpec(spec) : {};

  const body: Record<string, unknown> = { propertyGithubRepoId };
  const finalRole = role ?? parsed.role;
  const finalBranch = branch ?? parsed.branch;
  const finalSubdir = subdirectory ?? parsed.subdirectory;
  if (finalRole !== undefined) body.role = finalRole;
  if (finalBranch !== undefined) body.branch = finalBranch;
  if (finalSubdir !== undefined) body.subdirectory = finalSubdir;
  if (serviceName !== undefined) body.serviceName = serviceName;

  const result = await apiRequest<{ ok: boolean; data?: TemplateRepoLink; error?: string }>(
    apiUrl,
    token,
    'POST',
    `/org/environments/templates/${templateId}/repos`,
    body
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (json) console.log(JSON.stringify(result.data, null, 2));
  else console.log(`Linked repo to template ${templateId} (link=${result.data?.id})`);
}

async function handleRepoUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const templateId = getArgValue(argv, '--template');
  const linkId = getArgValue(argv, '--link-id');
  const role = getArgValue(argv, '--role');
  const branch = getArgValue(argv, '--branch');
  const subdirectory = getArgValue(argv, '--subdirectory');
  const serviceName = getArgValue(argv, '--service-name');

  if (!templateId || !linkId) {
    console.error('Error: --template and --link-id are required.');
    process.exit(1);
  }

  const body: Record<string, unknown> = {};
  if (role !== undefined) body.role = role;
  if (branch !== undefined) body.branch = branch;
  if (subdirectory !== undefined) body.subdirectory = subdirectory;
  if (serviceName !== undefined) body.serviceName = serviceName;

  const result = await apiRequest<{ ok: boolean; data?: TemplateRepoLink; error?: string }>(
    apiUrl,
    token,
    'PATCH',
    `/org/environments/templates/${templateId}/repos/${linkId}`,
    body
  );

  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (json) console.log(JSON.stringify(result.data, null, 2));
  else console.log(`Updated repo link ${linkId}`);
}

async function handleRepoRemove(argv: string[], apiUrl: string, token: string): Promise<void> {
  const json = hasFlag(argv, '--json');
  const templateId = getArgValue(argv, '--template');
  const linkId = getArgValue(argv, '--link-id');
  if (!templateId || !linkId) {
    console.error('Error: --template and --link-id are required.');
    process.exit(1);
  }
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/environments/templates/${templateId}/repos/${linkId}`
  );
  if (!result.ok) {
    if (json) console.log(JSON.stringify(result, null, 2));
    else console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (json) console.log(JSON.stringify(result, null, 2));
  else console.log(`Removed repo link ${linkId} from template ${templateId}`);
}

async function handleRepo(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, ...rest] = argv;
  switch (action) {
    case 'list':
      await handleRepoList(rest, apiUrl, token);
      break;
    case 'add':
      await handleRepoAdd(rest, apiUrl, token);
      break;
    case 'update':
      await handleRepoUpdate(rest, apiUrl, token);
      break;
    case 'remove':
    case 'rm':
      await handleRepoRemove(rest, apiUrl, token);
      break;
    default:
      console.error(
        `Unknown repo action: ${action ?? '(none)'}. Use list/add/update/remove.\n` +
          'Examples:\n' +
          '  canary sandbox repo list --template <template-id>\n' +
          '  canary sandbox repo add --template <template-id> --property-github-repo-id <uuid> --role frontend\n' +
          '  canary sandbox repo add --template <template-id> --property-github-repo-id <uuid> --spec frontend=owner/web@main:/apps/web'
      );
      process.exit(1);
  }
}

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

export async function runSandbox(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  // Guide is purely local — no auth needed
  if (subCommand === 'guide') {
    const section = rest.find((a) => !a.startsWith('-'));
    const json = hasFlag(rest, '--json');
    printGuide(SANDBOX_GUIDE_SECTIONS, section, json);
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  // Strip global flags consumed by resolveConfig so handlers don't see them as positional args
  const globalFlags = new Set(['--env', '--api-url', '--token', '--org']);
  const args = rest.filter((arg, i) => {
    if (globalFlags.has(arg)) return false;
    if (i > 0 && globalFlags.has(rest[i - 1])) return false;
    return true;
  });

  switch (subCommand) {
    case 'list':
      await handleList(args, apiUrl, token);
      break;
    case 'template':
      await handleTemplate(args, apiUrl, token);
      break;
    case 'repo':
      await handleRepo(args, apiUrl, token);
      break;
    case 'artifacts':
      await handleArtifacts(args, apiUrl, token);
      break;
    case 'checklist':
      await handleChecklist(args, apiUrl, token);
      break;
    case 'create':
      await handleCreate(args, apiUrl, token);
      break;
    case 'provision':
      await handleProvision(args, apiUrl, token);
      break;
    case 'warm':
      await handleWarm(args, apiUrl, token);
      break;
    case 'verify':
      await handleVerify(args, apiUrl, token);
      break;
    case 'set-size':
      await handleSetSize(args, apiUrl, token);
      break;
    case 'pool': {
      const [poolAction, ...poolRest] = args;
      if (poolAction === 'set-size') {
        await handlePoolSetSize(poolRest, apiUrl, token);
      } else {
        console.error(`Unknown pool action: ${poolAction}. Use "set-size".`);
        process.exit(1);
      }
      break;
    }
    case 'artifact': {
      const [action, ...actionRest] = args;
      if (action === 'get') {
        await handleArtifactGet(actionRest, apiUrl, token);
      } else if (action === 'create') {
        await handleArtifactCreate(actionRest, apiUrl, token);
      } else {
        console.error(`Unknown artifact action: ${action}. Use "get" or "create".`);
        process.exit(1);
      }
      break;
    }
    case 'config': {
      const [configAction, ...configRest] = args;
      if (configAction === 'list') {
        await handleConfigList(configRest, apiUrl, token);
      } else if (configAction === 'set') {
        await handleConfigSet(configRest, apiUrl, token);
      } else if (configAction === 'unset') {
        await handleConfigUnset(configRest, apiUrl, token);
      } else {
        console.error(`Unknown config action: ${configAction}. Use "list", "set", or "unset".`);
        process.exit(1);
      }
      break;
    }
    case 'emulation':
      await handleEmulation(args, apiUrl, token);
      break;
    case 'action': {
      const [actionSub, ...actionRest] = args;
      switch (actionSub) {
        case 'list':
          await handleActionList(actionRest, apiUrl, token);
          break;
        case 'get':
          await handleActionGet(actionRest, apiUrl, token);
          break;
        case 'create':
          await handleActionCreate(actionRest, apiUrl, token);
          break;
        case 'delete':
          await handleActionDelete(actionRest, apiUrl, token);
          break;
        case 'execute':
          await handleActionExecute(actionRest, apiUrl, token);
          break;
        case 'executions':
          await handleActionExecutions(actionRest, apiUrl, token);
          break;
        case 'execution': {
          const [execSub, ...execRest] = actionRest;
          if (execSub === 'get') {
            await handleActionExecutionGet(execRest, apiUrl, token);
          } else if (execSub === 'cancel') {
            await handleActionExecutionCancel(execRest, apiUrl, token);
          } else {
            console.error(`Unknown execution sub-command: ${execSub}. Use "get" or "cancel".`);
            process.exit(1);
          }
          break;
        }
        default:
          console.error(`Unknown action sub-command: ${actionSub}. Use "list", "get", "create", "delete", "execute", "executions", or "execution".`);
          process.exit(1);
      }
      break;
    }
    case 'build':
      await handleBuild(args, apiUrl, token);
      break;
    case 'run-command':
      await handleRunCommand(args, apiUrl, token);
      break;
    case 'diagnostics':
      await handleDiagnostics(args, apiUrl, token);
      break;
    case 'logs':
      await handleLogs(args, apiUrl, token);
      break;
    case 'terminate':
      await handleTerminate(args, apiUrl, token);
      break;
    case 'delete':
      await handleDelete(args, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printHelp();
      process.exit(1);
  }
}
